import { zodResolver } from "@hookform/resolvers/zod";
import { Col, Row } from "react-bootstrap";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { ProposedProjectController } from "services/proposedProject/controller";
import { DatePicker, FilePicker, Textarea, useAccordionWizard } from "stories";
import { AccordionForm } from "stories/components/accordionForm";
import { RadioGroupList } from "stories/components/radioGroup/RadioGroup";
import { useFileUpload } from "utils/customHooks/useFileUpload";
import { useToast } from "utils/customHooks/useToast";
import { z } from "zod";
import { staticEntities } from "../constants";
// import {useToast} from "utils/customHooks/useToast";

const proposedProjectController = ProposedProjectController();

const getGeoTechnicalReportModel = () => ({
  geotechnical_report_available: false,
  geotechnical_report_filename: "",
  report_synopsis: "",
  expected_obtaining_date: "",
});

const schema = z
  .object({
    geotechnical_report_available: z.boolean().nullable(),
    geotechnical_report_filename: z.any(),
    report_synopsis: z.any(),
    expected_obtaining_date: z.any(),
  })
  .refine(
    (data) =>
      data.geotechnical_report_available ||
      (!data.geotechnical_report_available &&
        typeof data.expected_obtaining_date === "string" &&
        data.expected_obtaining_date.length > 0),
    {
      message: "This field is required",
      path: ["expected_obtaining_date"],
    }
  );
export const GeotechnicalReportQueryKey = "geotechnical-report";
const GeotechnicalReport = () => {
  const {
    formState: { errors },
    ...form
  } = useForm({
    defaultValues: getGeoTechnicalReportModel(),
    resolver: zodResolver(schema),
  });
  const geoTechnicalReportAvailable = useWatch({
    control: form.control,
    name: "geotechnical_report_available",
  });
  const params = useParams();
  const queryClient = useQueryClient();
  const queryKey = [GeotechnicalReportQueryKey, { id: params.id }];
  const { showToast } = useToast();
  const { next } = useAccordionWizard();
  const { handleFileFields } = useFileUpload();
  const handleUpdate = async (values) => {
    return handleFileFields(values).then((values) => {
      values.opportunityId = params.id;
      return proposedProjectController.updateGeotechnicalReport(values);
    });
  };

  const { isLoading: fetchLoading } = useQuery({
    queryKey,
    queryFn: proposedProjectController.getGeotechnicalReport.bind(
      null,
      params.id
    ),
    onSuccess: form.reset,
  });

  const { mutate, isLoading: postLoading } = useMutation(handleUpdate, {
    onSuccess: () => {
      showToast("Geotechnical report updated!", "success");
      next();
      queryClient.invalidateQueries(queryKey);
    },
  });
  return (
    <AccordionForm
      accordionTitle="Geotechnical Report"
      accordionKey="geotechnical-report"
      form={form}
      isPostLoading={postLoading}
      isPreLoading={fetchLoading}
      onSubmit={mutate}
    >
      <Row>
        <Col xs={4}>
          <Controller
            control={form.control}
            name="geotechnical_report_available"
            render={({ field }) => {
              return (
                <RadioGroupList
                  label={"Geotechnical Report Available?"}
                  list={staticEntities.booleanList}
                  fullWidth
                  {...field}
                />
              );
            }}
          />
        </Col>
      </Row>
      {geoTechnicalReportAvailable ? (
        <>
          <Row>
            <Col xs={6}>
              <Textarea
                label={"Report Synopsis"}
                placeholder="Highlight the synopsis here and/or highlight the page number in
										the report with synopsis"
                {...form.register("report_synopsis")}
              />
            </Col>
            <Col xs={6}>
              <Controller
                control={form.control}
                name={`geotechnical_report_filename`}
                render={({ field }) => (
                  <FilePicker
                    multiple={false}
                    type="file"
                    label={"Geotechnical Report"}
                    {...field}
                  />
                )}
              />
            </Col>
          </Row>
        </>
      ) : (
        <Row>
          <Col xs={4}>
            <Controller
              control={form.control}
              name={`expected_obtaining_date`}
              render={({ field }) => {
                return (
                  <DatePicker
                    label="Expected Obtaining Date"
                    error={errors?.expected_obtaining_date?.message}
                    {...field}
                  />
                );
              }}
            />
          </Col>
        </Row>
      )}
    </AccordionForm>
  );
};

export default GeotechnicalReport;
